// Partner Program API client (P1). Reuses the app-wide axios instance from
// `@/lib/api` so every partner endpoint automatically carries the correct
// Bearer token (stored under `ariadni_token`) AND participates in the shared
// 401 → refresh interceptor. Do NOT roll a local axios instance here.
import { api } from "@/lib/api";

export const partnerApi = {
  me: () => api.get("/partner/me").then((r) => r.data),
  apply: (payload) => api.post("/partner/apply", payload).then((r) => r.data),
  signup: (payload) => api.post("/partner/signup", payload).then((r) => r.data),
  programConfig: () => api.get("/partner/program/config").then((r) => r.data),
  updatePayout: (payload) => api.put("/partner/me/payout", payload).then((r) => r.data),
  dashboard: () => api.get("/partner/dashboard").then((r) => r.data),
  customers: (params) => api.get("/partner/customers", { params }).then((r) => r.data),
  commissions: (params) => api.get("/partner/commissions", { params }).then((r) => r.data),
  marketingLink: () => api.get("/partner/marketing/link").then((r) => r.data),
  // P2: promo codes
  campaigns: () => api.get("/partner/campaigns").then((r) => r.data),
  codes: () => api.get("/partner/codes").then((r) => r.data),
  generateCode: (payload) => api.post("/partner/codes", payload).then((r) => r.data),
  deactivateCode: (id) => api.post(`/partner/codes/${id}/deactivate`).then((r) => r.data),
  // P3: payouts + marketing
  payouts: (params) => api.get("/partner/payouts", { params }).then((r) => r.data),
  payoutDetail: (id) => api.get(`/partner/payouts/${id}`).then((r) => r.data),
  marketingKit: () => api.get("/partner/marketing/kit").then((r) => r.data),
  acceptTos: (tos_version) => api.post("/partner/accept-tos", { tos_version }).then((r) => r.data),
};

export const partnerAdminApi = {
  list: (params) => api.get("/admin/partners", { params }).then((r) => r.data),
  detail: (id) => api.get(`/admin/partners/${id}`).then((r) => r.data),
  approve: (id) => api.post(`/admin/partners/${id}/approve`).then((r) => r.data),
  reject: (id, reason) => api.post(`/admin/partners/${id}/reject`, { reason }).then((r) => r.data),
  suspend: (id, reason) => api.post(`/admin/partners/${id}/suspend`, { reason }).then((r) => r.data),
  reactivate: (id) => api.post(`/admin/partners/${id}/reactivate`).then((r) => r.data),
  revoke: (id, reason) => api.post(`/admin/partners/${id}/revoke`, { reason }).then((r) => r.data),
  addNote: (id, body) => api.post(`/admin/partners/${id}/notes`, { body }).then((r) => r.data),
  tierOverride: (id, rate_bps) =>
    api.post(`/admin/partners/${id}/tier-override`, { rate_bps }).then((r) => r.data),
  commissions: (params) => api.get("/admin/partner-commissions", { params }).then((r) => r.data),
  approveCommission: (id, reason) =>
    api.post(`/admin/partner-commissions/${id}/approve`, { reason }).then((r) => r.data),
  reverseCommission: (id, reason) =>
    api.post(`/admin/partner-commissions/${id}/reverse`, { reason }).then((r) => r.data),
  // P2: promo campaigns
  listCampaigns: (params) => api.get("/admin/partner-campaigns", { params }).then((r) => r.data),
  createCampaign: (payload) => api.post("/admin/partner-campaigns", payload).then((r) => r.data),
  deactivateCampaign: (id) =>
    api.post(`/admin/partner-campaigns/${id}/deactivate`).then((r) => r.data),
  listCampaignCodes: (id) =>
    api.get(`/admin/partner-campaigns/${id}/codes`).then((r) => r.data),
  adminDeactivateCode: (id) =>
    api.post(`/admin/partner-codes/${id}/deactivate`).then((r) => r.data),
  // P3: payouts
  planPayouts: (payload) => api.post("/admin/partner-payouts/plan", payload).then((r) => r.data),
  commitPayout: (payload) => api.post("/admin/partner-payouts", payload).then((r) => r.data),
  markPayoutPaid: (id, external_ref) =>
    api.post(`/admin/partner-payouts/${id}/mark-paid`, { external_ref }).then((r) => r.data),
  cancelPayout: (id, reason) =>
    api.post(`/admin/partner-payouts/${id}/cancel`, { reason }).then((r) => r.data),
  listPayouts: (params) => api.get("/admin/partner-payouts", { params }).then((r) => r.data),
  payoutDetail: (id) => api.get(`/admin/partner-payouts/${id}`).then((r) => r.data),
  // P3: marketing assets
  listMarketingAssets: () => api.get("/admin/partner-marketing-assets").then((r) => r.data),
  addMarketingAsset: (payload) =>
    api.post("/admin/partner-marketing-assets", payload).then((r) => r.data),
  deleteMarketingAsset: (id) =>
    api.delete(`/admin/partner-marketing-assets/${id}`).then((r) => r.data),
};

export const bpsToPct = (bps) => `${(Number(bps || 0) / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
export const centsToUsd = (c, cur = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format((Number(c) || 0) / 100);

export const TOS_VERSION_CURRENT = "2026-08-27";
